import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import classNames from "classnames";
import {
  menuDeleteIcon,
  menuDuplicateIcon,
  viewIcon,
  dbSidePanelIcon,
} from "../../assets/icons";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import {
  EmptyLayout,
  Footer,
  SearchBar,
  Card,
  Pagination,
} from "../../components/molecules";
import {
  selectDataModels,
  deleteDataModel,
  selectDataModelsFilters,
} from "./index";
import {
  POPOVER_BUTTONS,
  FOOTER_CONSTANTS,
  BADGE_VARIANTS,
  DEFAULT_LIMIT,
  ONE,
} from "../../constants";
import { actions } from "./dataModels.reducers";
import { actions as appActions } from "../Applications/applications.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { actions as drawerActions } from "../../components/organisms/Drawer/drawers.reducers";
import {
  DRAWERS,
  GLOBAL_INFRASTRUCTURE_REGIONS,
} from "../../components/organisms/AppDrawers/drawers.constants";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { actions as spinnerActions } from "../../components/organisms/Spinner/spinner.reducers";
import { selectAreResourcesPending } from "./dataModels.selectors";

const { addSpinner } = spinnerActions;
const { DELETE, DUPLICATE, VIEW } = POPOVER_BUTTONS;
const { DATA_MODELS_TITLE, DATA_MODELS_SUBTITLE } = FOOTER_CONSTANTS;
const { DELETE_MODAL } = MODALS;
const { PRIMARY, SUCCESS } = BADGE_VARIANTS;

const {
  setSelectedDataModelId,
  setSelectedDataModel,
  setDataModelsFilters,
  setSelectedDataTable,
  setShouldShowLoadingPage,
  resetDataModelDetails,
} = actions;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { MANAGE_DATA_MODEL_DRAWER } = DRAWERS;
const { setShouldShowMenuBar } = appActions;
const { setSelectedTab } = breadCrumbActions;
const { setSelectedDrawer } = drawerActions;
const {
  DELETE_DATA_MODEL,
  FETCH_DATA_MODELS,
  LOADING_PAGE,
  CREATE_DATA_MODEL,
} = SPINNER_NAMES;

const DataModels = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [selectDataModel, setSelectDataModel] = useState();
  const [pageCount, setPageCount] = useState(1);
  const allDataModels = useSelector(selectDataModels);
  const areResourcesPending = useSelector(selectAreResourcesPending);
  const filters = useSelector(selectDataModelsFilters);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);

  const { data: dataModelsdata, count, total: totalDataModels } = allDataModels;
  const currentPath = window.location.pathname;

  useEffect(() => {
    setPageCount(Math.ceil(count / DEFAULT_LIMIT));
  }, [count]);

  useEffect(() => {
    if (selectDataModel) {
      const id = selectDataModel.id;
      dispatch(setShouldShowLoadingPage(true));
      dispatch(addSpinner(LOADING_PAGE));
      navigate(`/data-models/${id}/collections`);
    }
  }, [selectDataModel, dispatch, navigate]);

  useEffect(() => {
    !areResourcesPending && dispatch(setSelectedDrawer());
  }, [areResourcesPending, dispatch]);

  const popOverOptions = (dataModel) => [
    {
      name: VIEW,
      icon: viewIcon,
      handleOnClick: () => {
        setSelectDataModel(dataModel);
      },
    },
    {
      name: DUPLICATE,
      icon: menuDuplicateIcon,
      handleOnClick: () => console.log("Clicked duplicate"),
    },
    {
      name: DELETE,
      icon: menuDeleteIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => {
        dispatch(setSelectedModal(DELETE_MODAL));
        dispatch(setItemToBeDeleted(dataModel));
        setShouldShowDeleteModal(true);
      },
    },
  ];

  useEffect(() => {
    dispatch(setSelectedDataTable(null));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setDataModelsFilters());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setSelectedDataModelId(null));
    dispatch(setSelectedDataModel(null));
    dispatch(setSelectedTab(null));
    dispatch(setShouldShowMenuBar(false));
    dispatch(resetDataModelDetails());
  }, [dispatch, currentPath]);

  const handleDeleteDataModel = useCallback(
    (dataModelToBeDeleted) => {
      dispatch(deleteDataModel(dataModelToBeDeleted.id)).then((res) => {
        if (res.payload) {
          dispatch(setSelectedModal(null));
          setShouldShowDeleteModal(false);
        }
      });
    },
    [dispatch]
  );

  const handlePagination = useCallback(
    (value) =>
      dispatch(setDataModelsFilters({ ...filters, page: value + ONE })),
    [dispatch, filters]
  );

  const handleOnSearch = useCallback(
    (value) =>
      dispatch(
        setDataModelsFilters({ ...filters, name: value.trim(), page: ONE })
      ),
    [dispatch, filters]
  );

  const handleShouldShowDrawer = useCallback(
    () => dispatch(setSelectedDrawer(MANAGE_DATA_MODEL_DRAWER)),
    [dispatch]
  );
  const getBadges = (dataModel) => [
    {
      color: SUCCESS,
      value: dataModel.status,
    },
    {
      color: PRIMARY,
      value: GLOBAL_INFRASTRUCTURE_REGIONS.find(
        (each) => each.value === dataModel.region.toLowerCase()
      ).name,
    },
    {
      color: PRIMARY,
      value: dataModel.billingType || "free",
    },
  ];

  const renderDataModelsLayout = () => {
    return dataModelsdata.length ? (
      <>
        <div className="min-h-[70vh] mb-4">
          <div className="grid grid-cols-12 gap-4 w-full">
            {dataModelsdata.map((dataModel) => (
              <Card
                key={dataModel.id}
                name={dataModel.name}
                badges={getBadges(dataModel)}
                createdAt={dataModel.createdAt}
                popOverOptions={popOverOptions(dataModel)}
                onClick={() => setSelectDataModel(dataModel)}
              />
            ))}
          </div>
        </div>
        {shouldShowDeleteModal && (
          <DeleteConfirmationModal
            handleDelete={handleDeleteDataModel}
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            title="Delete Database"
            spinner={DELETE_DATA_MODEL}
          />
        )}
      </>
    ) : (
      <EmptyLayout
        title="No Database"
        subTitle="You can get started by creating a new Database."
        buttonText="Create New Database"
        image={dbSidePanelIcon}
        handleOnClick={handleShouldShowDrawer}
      />
    );
  };

  return (
    <div className="scrollbar content bg-gray-900 overflow-auto p-6 flex flex-1 flex-col">
      <Helmet>
        <title>DataModels | TavaJS</title>
      </Helmet>
      <div
        className={classNames(
          "flex-col md:flex-row flex mb-4 gap-4 justify-end",
          { "justify-between": !!totalDataModels }
        )}
      >
        {!!totalDataModels && (
          <SearchBar placeholder="Search" handleOnSearch={handleOnSearch} />
        )}
        <div className=" flex flex-col md:flex-row gap-4">
          {/* <button
              type="button"
              className="rounded-md h-[38px] py-2 px-4 shadow-sm text-sm leading-5 font-medium text-white outline outline-1 outline-indigo-700"
            >
              Import DataModel
            </button> */}
          <button
            type="button"
            className="btn btn-primary"
            disabled={isSpinnerActive}
            onClick={handleShouldShowDrawer}
          >
            <Spinner
              name={CREATE_DATA_MODEL}
              setIsSpinnerActive={setIsSpinnerActive}
              persistSize
            >
              Create New Database
            </Spinner>
          </button>
        </div>
      </div>
      <div className="justify-between flex flex-col flex-1">
        <Spinner name={FETCH_DATA_MODELS}>{renderDataModelsLayout()}</Spinner>
        <Pagination
          handlePagination={handlePagination}
          pagesCount={pageCount}
          activePage={filters.page - ONE}
          count={count}
        />
        <Footer title={DATA_MODELS_TITLE} subtitle={DATA_MODELS_SUBTITLE} />
      </div>
    </div>
  );
};

export default DataModels;
